/**
 * Location Permission Prompt Component
 * 
 * Context-driven location permission request UI component for TabPicker.
 * Provides clear value proposition and graceful fallback options.
 * 
 * Features:
 * - Context-aware messaging
 * - Clear benefit explanation
 * - Privacy-focused approach
 * - Fallback option always available
 * 
 * @module LocationPrompt
 */

import React, { useState } from 'react';
import { LocationContext, LocationService, type LocationContextType } from '@/utils/locationServices';

interface LocationPromptProps {
  context: LocationContext;
  contextType: LocationContextType;
  onLocationGranted: (position: GeolocationPosition) => void;
  onLocationDenied: () => void;
  onFallback: () => void;
  className?: string;
  isVisible?: boolean;
}

export const LocationPrompt: React.FC<LocationPromptProps> = ({
  context,
  contextType,
  onLocationGranted,
  onLocationDenied,
  onFallback,
  className = '',
  isVisible = true
}) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAllowLocation = async () => {
    setIsRequesting(true);
    setError(null);

    try {
      // Use LocationService directly for now to avoid store integration issues
      console.log('🔍 LocationPrompt: Requesting location permission...');
      const position = await LocationService.requestLocation(context);
      
      if (position) {
        console.log('✅ LocationPrompt: Permission granted, position received');
        onLocationGranted(position);
      } else {
        console.log('❌ LocationPrompt: Permission denied or failed');
        onLocationDenied();
      }
    } catch (err) {
      console.error('❌ LocationPrompt: Location request error:', err);
      setError('Unable to access location. Please check your browser settings.');
      onLocationDenied();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDenyLocation = () => {
    onLocationDenied();
  };

  const handleUseFallback = () => {
    onFallback();
  };

  if (!isVisible) return null;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-6 max-w-md mx-auto ${className}`}>
      {/* Icon */}
      <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4">
        <svg 
          className="w-6 h-6 text-blue-600" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
          />
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
          />
        </svg>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 text-center mb-3">
        {context.purpose}
      </h3>

      {/* Benefits */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">This will help you:</p>
        <ul className="text-sm text-gray-600 space-y-1">
          {context.benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <svg 
                className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" 
                fill="currentColor" 
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path 
                  fillRule="evenodd" 
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                  clipRule="evenodd" 
                />
              </svg>
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      {/* Privacy notice */}
      <div className="bg-gray-50 rounded-md p-3 mb-4">
        <div className="flex">
          <svg 
            className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
              clipRule="evenodd" 
            />
          </svg>
          <p className="text-xs text-gray-600">
            Your location is fuzzed for privacy and only used to find nearby games. 
            You can change these settings anytime.
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-3">
        <button
          onClick={handleAllowLocation}
          disabled={isRequesting}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isRequesting ? (
            <div className="flex items-center justify-center">
              <svg 
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" 
                fill="none" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Requesting location...
            </div>
          ) : (
            'Allow Location Access'
          )}
        </button>

        <button
          onClick={handleUseFallback}
          disabled={isRequesting}
          className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
        >
          {context.fallback}
        </button>

        <button
          onClick={handleDenyLocation}
          disabled={isRequesting}
          className="w-full text-gray-500 text-sm py-1 hover:text-gray-700 focus:outline-none focus:underline disabled:opacity-50 transition-colors"
        >
          Don't ask again
        </button>
      </div>
    </div>
  );
};

/**
 * Modal wrapper for LocationPrompt
 * 
 * Provides modal overlay and positioning for the location prompt.
 */
interface LocationPromptModalProps extends LocationPromptProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LocationPromptModal: React.FC<LocationPromptModalProps> = ({
  isOpen,
  onClose,
  onLocationDenied,
  onFallback,
  ...props
}) => {
  const handleLocationDenied = () => {
    onLocationDenied();
    onClose();
  };

  const handleFallback = () => {
    onFallback();
    onClose();
  };

  const handleLocationGranted = (position: GeolocationPosition) => {
    props.onLocationGranted(position);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal content */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <LocationPrompt
          {...props}
          onLocationGranted={handleLocationGranted}
          onLocationDenied={handleLocationDenied}
          onFallback={handleFallback}
          className="relative"
        />
      </div>
    </div>
  );
};

export default LocationPrompt;